import { useCallback, useState } from "react";

import Box from "ds/components/Box";
import CardCollapsibleWrapper from "ds/components/Card/Collapsible/components/Wrapper";
import CardCollapsibleToggle from "ds/components/Card/Collapsible/components/Toggle";
import CardCollapsibleContent from "ds/components/Card/Collapsible/components/Content";
import CardHeader from "ds/components/Card/components/Header";

import { ChangeHistoryEntry, InputSnapshot } from "../types";
import RollbackButton from "../RollbackButton";
import YamlDiffPanel from "../YamlDiffPanel";
import formatChangeHistoryCardEntryTime from "./helpers";
import ChangeHistoryCardFieldChangeLine from "./components/FieldChangeLine";
import ChangeHistoryCardSummary from "./Summary";

type ChangeHistoryCardProps = {
  entry: ChangeHistoryEntry;
  templateId: string;
  deploymentId: string;
  currentInputs: InputSnapshot[];
  currentVersionNumber: string;
  isLatest: boolean;
};

const ChangeHistoryCard = ({ entry, templateId, deploymentId, currentInputs, currentVersionNumber, isLatest }: ChangeHistoryCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), []);
  const hasYamlDiff =
    entry.versionChange?.previousBody &&
    entry.versionChange?.currentBody &&
    entry.versionChange.previousBody !== entry.versionChange.currentBody;
  const isExpandable = entry.inputChanges.length > 1 || !!hasYamlDiff;
  const timeLabel = formatChangeHistoryCardEntryTime(entry.timestamp);

  return (
    <CardCollapsibleWrapper isCollapsed={!isExpanded} onToggle={toggleExpanded}>
      <CardHeader
        suffix={
          !isLatest && (
            <RollbackButton entry={entry} templateId={templateId} deploymentId={deploymentId} currentInputs={currentInputs} currentVersionNumber={currentVersionNumber} />
          )
        }
      >
        <Box align="center" gap="medium">
          {isExpandable && (
            <CardCollapsibleToggle ariaLevel={3} ariaLabel="Toggle change details" />
          )}
          <ChangeHistoryCardSummary entry={entry} timeLabel={timeLabel} />
        </Box>
      </CardHeader>
      {isExpanded && (
        <CardCollapsibleContent hasHeader fullbleed>
          <Box gap="medium" padding="0 x-large x-large" direction="column">
            {entry.inputChanges.map((change) => (
              <ChangeHistoryCardFieldChangeLine key={change.fieldId} change={change} />
            ))}

            {hasYamlDiff && (
              <YamlDiffPanel
                original={entry.versionChange!.previousBody!}
                modified={entry.versionChange!.currentBody!}
              />
            )}
          </Box>
        </CardCollapsibleContent>
      )}
    </CardCollapsibleWrapper>
  );
};

export default ChangeHistoryCard;
